import { useState, useCallback } from 'react';
import {
  calculateLineAmounts,
  isMarkupRate,
  PROCUREMENT_DEFAULT_CHARGE_PERCENT,
  resolveQuoteItemRates
} from '../utils/quoteItemAmounts';

export interface QuoteItem {
  id?: string;
  quote_id?: string;
  service_type: string;
  item_name: string;
  list_price: number;
  unit_price?: number; // 牌價（從 service_rate 取得，當年度）
  contract_price: number;
  quantity: number;
  unit: string;
  is_deduction?: boolean; // 是否為扣除項目（負值）
  // 新增的自動計算欄位
  subtotal?: number; // 小計金額（自動計算：unit_price * quantity）
  subtotal_discount?: number; // 折扣金額（自動計算：(unit_price - contract_price) * quantity）
  subtotal_afterdiscount?: number; // 折扣後小計金額（自動計算：contract_price * quantity）
}

export interface ServiceRate {
  id: string;
  service_type: string;
  unit_price?: number;
  unit?: string;
  year?: number;
  is_default?: boolean;
  description?: string;
  hourly_rate?: number;
  charge_type?: 'fixed' | 'percentage';
}

export interface ContractServiceItem {
  id: string;
  contract_id: string;
  service_type: string;
  contract_price: number;
  unit: string;
}

/** 用於合約 / 服務收費表之間比對服務類型（忽略前後空白與全形空白的細微差異、大小寫差異） */
export function normalizeServiceTypeKey(s: string | undefined): string {
  if (!s) return '';
  return s.replace(/[\s\u3000]+/g, ' ').trim().toLowerCase();
}

export const useQuoteItems = (onItemsChange?: (items: QuoteItem[]) => void) => {
  const [quoteItems, setQuoteItems] = useState<QuoteItem[]>([]);

  const appendQuoteItem = useCallback((newItem: QuoteItem) => {
    setQuoteItems(prev => {
      const newItems = [...prev, newItem];
      onItemsChange?.(newItems);
      return newItems;
    });
  }, [onItemsChange]);

  // 新增工時／計次報價項目
  const addQuoteItem = useCallback(() => {
    appendQuoteItem({
      service_type: '',
      item_name: '',
      list_price: 0,
      unit_price: 0,
      contract_price: 0,
      quantity: 1,
      unit: '小時',
      is_deduction: false,
      subtotal: 0,
      subtotal_discount: 0,
      subtotal_afterdiscount: 0
    });
  }, [appendQuoteItem]);

  // 新增採購／倍率報價項目（quantity 存實際採購金額）
  const addMarkupQuoteItem = useCallback(() => {
    appendQuoteItem({
      service_type: '',
      item_name: '',
      list_price: PROCUREMENT_DEFAULT_CHARGE_PERCENT,
      unit_price: PROCUREMENT_DEFAULT_CHARGE_PERCENT,
      contract_price: PROCUREMENT_DEFAULT_CHARGE_PERCENT,
      quantity: 0,
      unit: '%',
      is_deduction: false,
      subtotal: 0,
      subtotal_discount: 0,
      subtotal_afterdiscount: 0
    });
  }, [appendQuoteItem]);

  const applyItemAmounts = (item: QuoteItem): QuoteItem => {
    const unitPrice = item.unit_price ?? item.list_price ?? 0;
    const amounts = calculateLineAmounts({
      listPrice: unitPrice,
      contractPrice: item.contract_price ?? 0,
      quantity: item.quantity ?? 0,
      unit: item.unit,
      forceMarkup: isMarkupRate({ unit: item.unit }),
      isDeduction: item.is_deduction
    });
    return {
      ...item,
      subtotal: amounts.subtotal,
      subtotal_discount: amounts.subtotal_discount,
      subtotal_afterdiscount: amounts.subtotal_afterdiscount
    };
  };

  const patchQuoteItem = useCallback((index: number, patch: Partial<QuoteItem>) => {
    setQuoteItems(prevItems => {
      const updatedItems = [...prevItems];
      const merged = applyItemAmounts({ ...updatedItems[index], ...patch });
      updatedItems[index] = merged;
      onItemsChange?.(updatedItems);
      return updatedItems;
    });
  }, [onItemsChange]);

  // 更新報價項目
  const updateQuoteItem = useCallback((index: number, field: keyof QuoteItem, value: string | number | boolean) => {
    setQuoteItems(prevItems => {
      const updatedItems = [...prevItems];
      const updatedItem = { ...updatedItems[index], [field]: value };
      if (field === 'contract_price' && isMarkupRate({ unit: updatedItem.unit })) {
        const percent = Number(value) || 0;
        updatedItem.list_price = percent;
        updatedItem.unit_price = percent;
      }
      
      // 如果更新了價格、單位或數量相關欄位，自動重新計算
      if (['unit_price', 'list_price', 'contract_price', 'quantity', 'is_deduction', 'unit'].includes(field)) {
        const unitPrice = updatedItem.unit_price ?? updatedItem.list_price ?? 0;
        const amounts = calculateLineAmounts({
          listPrice: unitPrice,
          contractPrice: updatedItem.contract_price ?? 0,
          quantity: updatedItem.quantity ?? 0,
          unit: updatedItem.unit,
          forceMarkup: isMarkupRate({ unit: updatedItem.unit }),
          isDeduction: updatedItem.is_deduction
        });
        updatedItem.subtotal = amounts.subtotal;
        updatedItem.subtotal_discount = amounts.subtotal_discount;
        updatedItem.subtotal_afterdiscount = amounts.subtotal_afterdiscount;
      }
      
      updatedItems[index] = updatedItem;
      onItemsChange?.(updatedItems);
      return updatedItems;
    });
  }, [onItemsChange]);

  // 移除報價項目
  const removeQuoteItem = useCallback((index: number) => {
    setQuoteItems(prev => {
      const newItems = prev.filter((_, i) => i !== index);
      onItemsChange?.(newItems);
      return newItems;
    });
  }, [onItemsChange]);

  // 批量更新報價項目
  const setQuoteItemsBatch = useCallback((items: QuoteItem[]) => {
    setQuoteItems(items);
    onItemsChange?.(items);
  }, [onItemsChange]);

  // 清空所有報價項目
  const clearQuoteItems = useCallback(() => {
    setQuoteItems([]);
    onItemsChange?.([]);
  }, [onItemsChange]);

  // 計算小計金額（工時為牌價 × 數量；採購為實際金額 × 收費比例）
  const calculateSubtotal = useCallback((items: QuoteItem[]) => {
    return items.reduce((sum, item) => {
      const unitPrice = item.unit_price ?? item.list_price ?? 0;
      const amounts = calculateLineAmounts({
        listPrice: unitPrice,
        contractPrice: item.contract_price ?? 0,
        quantity: item.quantity ?? 0,
      unit: item.unit,
      forceMarkup: isMarkupRate({ unit: item.unit }),
      isDeduction: item.is_deduction
      });
      return sum + amounts.subtotal;
    }, 0);
  }, []);

  // 計算折扣總金額（倍率 110% 時為負值，代表服務費加價）
  const calculateTotalDiscount = useCallback((items: QuoteItem[]) => {
    return items.reduce((sum, item) => {
      const unitPrice = item.unit_price ?? item.list_price ?? 0;
      const amounts = calculateLineAmounts({
        listPrice: unitPrice,
        contractPrice: item.contract_price ?? 0,
        quantity: item.quantity ?? 0,
      unit: item.unit,
      forceMarkup: isMarkupRate({ unit: item.unit }),
      isDeduction: item.is_deduction
      });
      return sum + amounts.subtotal_discount;
    }, 0);
  }, []);

  // 計算折扣後小計金額（合約倍率／單價套用數量）
  const calculateSubtotalAfterDiscount = useCallback((items: QuoteItem[]) => {
    return items.reduce((sum, item) => {
      const unitPrice = item.unit_price ?? item.list_price ?? 0;
      const amounts = calculateLineAmounts({
        listPrice: unitPrice,
        contractPrice: item.contract_price ?? 0,
        quantity: item.quantity ?? 0,
      unit: item.unit,
      forceMarkup: isMarkupRate({ unit: item.unit }),
      isDeduction: item.is_deduction
      });
      return sum + amounts.subtotal_afterdiscount;
    }, 0);
  }, []);

  // 計算單一項目的所有金額欄位
  const calculateItemTotals = useCallback((item: QuoteItem): QuoteItem => {
    const unitPrice = item.unit_price ?? item.list_price ?? 0;
    const amounts = calculateLineAmounts({
      listPrice: unitPrice,
      contractPrice: item.contract_price ?? 0,
      quantity: item.quantity ?? 0,
      unit: item.unit,
      forceMarkup: isMarkupRate({ unit: item.unit }),
      isDeduction: item.is_deduction
    });
    return {
      ...item,
      subtotal: amounts.subtotal,
      subtotal_discount: amounts.subtotal_discount,
      subtotal_afterdiscount: amounts.subtotal_afterdiscount
    };
  }, []);

  // 計算稅金（基於折扣後總金額）
  const calculateTax = useCallback((discountedTotal: number, taxRate: number = 5) => {
    return Math.round(discountedTotal * (taxRate / 100));
  }, []);

  // 計算含稅總金額（折扣後總金額 + 稅金）
  const calculateTotalAmount = useCallback((discountedTotal: number, taxAmount: number) => {
    return Math.round(discountedTotal + taxAmount);
  }, []);

  // 根據服務類型更新價格
  const updateItemPrices = useCallback((
    index: number,
    serviceType: string,
    serviceRates: ServiceRate[],
    contractServiceItems: ContractServiceItem[],
    contractId?: string
  ) => {
    if (!serviceType) {
      patchQuoteItem(index, {
        service_type: '',
        list_price: 0,
        unit_price: 0,
        contract_price: 0,
        unit: '小時'
      });
      return;
    }

    // 取得當年度服務收費表
    const currentYear = new Date().getFullYear();
    
    // 優先查找當年度服務收費
    const currentYearRate = serviceRates.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rate => rate.service_type === serviceType && (rate as any).year === currentYear
    );
    
    const selectedRate = currentYearRate || (serviceRates
      .filter(rate => rate.service_type === serviceType)
      .sort((a, b) => ((b.year ?? 0) - (a.year ?? 0)))[0]);
    let unitPrice = 0;
    let unit = '小時';
    if (selectedRate) {
      unitPrice = selectedRate.unit_price || selectedRate.hourly_rate || 0;
      unit = selectedRate.charge_type === 'percentage' ? '%' : (selectedRate.unit || '小時');
    }

    let hasContractMatch = false;
    let contractStored = unitPrice;
    let contractUnit = unit;

    if (contractId) {
      const want = normalizeServiceTypeKey(serviceType);
      const contractItem = contractServiceItems.find(item => {
        const idOk =
          !item.contract_id || String(item.contract_id) === String(contractId);
        if (!idOk) return false;
        return normalizeServiceTypeKey(item.service_type) === want;
      });

      if (contractItem) {
        hasContractMatch = true;
        contractStored = contractItem.contract_price;
        contractUnit = selectedRate?.charge_type === 'percentage'
          ? '%'
          : (contractItem.unit || unit);
      }
    }

    const resolved = resolveQuoteItemRates({
      listPrice: unitPrice,
      listUnit: unit,
      contractPrice: contractStored,
      contractUnit,
      hasContractMatch
    });
    const markup = isMarkupRate({
      unit: resolved.unit,
      chargeType: selectedRate?.charge_type
    });

    setQuoteItems(prevItems => {
      const updatedItems = [...prevItems];
      const prev = updatedItems[index];
      if (!prev) return prevItems;
      const quantity = markup && prev.service_type !== serviceType ? 0 : prev.quantity;
      const merged = applyItemAmounts({
        ...prev,
        service_type: serviceType,
        list_price: resolved.listPrice,
        unit_price: resolved.listPrice,
        contract_price: resolved.contractPrice,
        unit: resolved.unit,
        quantity
      });
      updatedItems[index] = merged;
      onItemsChange?.(updatedItems);
      return updatedItems;
    });
  }, [onItemsChange, patchQuoteItem]);

  return {
    quoteItems,
    addQuoteItem,
    addMarkupQuoteItem,
    updateQuoteItem,
    removeQuoteItem,
    setQuoteItemsBatch,
    clearQuoteItems,
    calculateSubtotal,
    calculateTotalDiscount,
    calculateSubtotalAfterDiscount,
    calculateTax,
    calculateTotalAmount,
    calculateItemTotals,
    updateItemPrices
  };
}; 